/* eslint-disable no-param-reassign */
import {
  Editor, Element, Node, Path, Range, Text, Transforms,
} from 'slate';
import { CustomEditor, ElementTypes } from 'src/types/CustomSlateTypes';
import isList from './isList';

const withPlugin = (editor: CustomEditor) => {
  const {
    normalizeNode, isInline, isVoid, insertBreak, deleteBackward, setFragmentData,
  } = editor;

  editor.isInline = (element) => {
    if ([ElementTypes.link, ElementTypes.mention].includes(element.type)) return true;
    return isInline(element);
  };

  editor.isVoid = (element) => {
    if ([
      ElementTypes.image,
      ElementTypes.mention,
      ElementTypes.quote,
    ].includes(element.type)) return true;
    return isVoid(element);
  };

  // Prevent copy/cut and pasting of images
  editor.setFragmentData = (data, originEvent) => {
    const fragment = editor.getFragment();
    const hasVoid = fragment.some((node) => Element.isElement(node) && Editor.isVoid(editor, node));
    if (hasVoid && originEvent !== 'drag') {
      return;
    }

    setFragmentData(data, originEvent);
  };

  // if current selection is void node, insert a default node below
  editor.insertBreak = () => {
    if (!editor.selection || !Range.isCollapsed(editor.selection)) {
      insertBreak();
      return;
    }

    const selectedNodePath = Path.parent(editor.selection.anchor.path);
    const selectedNode = Node.get(editor, selectedNodePath);
    if (Element.isElement(selectedNode) && Editor.isVoid(editor, selectedNode)) {
      Editor.insertNode(editor, {
        type: ElementTypes.paragraph,
        children: [{ text: '' }],
      });
      return;
    }

    insertBreak();
  };

  editor.deleteBackward = (textUnit) => {
    if (
      !editor.selection
      || !Range.isCollapsed(editor.selection)
      || editor.selection.anchor.offset !== 0
    ) {
      deleteBackward(textUnit);
      return;
    }

    const parentPath = Path.parent(editor.selection.anchor.path);
    const parentNode = Node.get(editor, parentPath);
    const parentIsEmpty = Node.string(parentNode).length === 0;
    const { selection } = editor;

    // if prev node is a void node, remove the current node and select the void node
    if (parentIsEmpty && Path.hasPrevious(parentPath)) {
      const prevNodePath = Path.previous(parentPath);
      const prevNode = Node.get(editor, prevNodePath);
      if (Element.isElement(prevNode) && Editor.isVoid(editor, prevNode)) {
        Transforms.removeNodes(editor);
        Transforms.select(editor, prevNodePath);
        return;
      }
    }

    const [match] = Editor.nodes(editor, {
      match: (n) => !Editor.isEditor(n)
        && Element.isElement(n)
        && n.type === ElementTypes.listItem,
    });

    // is list-item and is at the begining of the block
    if (match
        && Editor.isStart(editor, editor.selection.anchor, match[1])
        && Range.isCollapsed(selection as Range)
    ) {
      Editor.withoutNormalizing(editor, () => {
        Transforms.unwrapNodes(editor, {
          match: (n) => !Editor.isEditor(n)
            && Element.isElement(n)
            && isList(n.type),
          split: true,
        });

        Transforms.setNodes<Element>(editor, { type: ElementTypes.paragraph }, {
          match: (n) => !Editor.isEditor(n)
            && Element.isElement(n)
            && n.type === ElementTypes.listItem,
        });
      });
      return;
    }

    deleteBackward(textUnit);
  };

  editor.normalizeNode = (entry) => {
    const [node, path] = entry;

    // Ensure that Editor's children can not be empty
    if (Editor.isEditor(node) && node.children.length === 0) {
      Transforms.deselect(editor);
      Transforms.insertNodes(
        editor,
        { type: ElementTypes.paragraph, children: [{ text: '' }] },
        { at: [0] },
      );
    }

    // Ensure voids are never wrapped
    if (Element.isElement(node) && Editor.isVoid(editor, node) && !Editor.isInline(editor, node)) {
      const [parentNode] = Editor.parent(editor, path);
      if (!Editor.isEditor(parentNode)) {
        Transforms.unwrapNodes(editor, {
          at: path,
          match: (n) => !Editor.isEditor(n)
            && Element.isElement(n)
            && !Editor.isVoid(editor, n),
          split: true,
        });
        return;
      }
    }

    // Remove links with no text
    if (Element.isElement(node) && node.type === ElementTypes.link) {
      if (Editor.hasTexts(editor, node) && Editor.string(editor, path).trim() === '') {
        Transforms.unwrapNodes(editor, { at: path });
      }
    }

    // If text is not wrapped around an element, wrap it with a paragraph
    if (Text.isText(node)) {
      const [parentNode] = Editor.parent(editor, path);
      if (!Element.isElement(parentNode)) {
        Transforms.wrapNodes(editor, { type: ElementTypes.paragraph, children: [] }, { at: path });
        return;
      }
    }

    // If list-item is not wrapped around a list block, change it to a paragraph
    if (Element.isElement(node) && node.type === ElementTypes.listItem) {
      const [parentNode] = Editor.parent(editor, path) as [Element, Path];
      if (!isList(parentNode.type)) {
        Transforms.setNodes<Element>(editor, { type: ElementTypes.paragraph }, { at: path });
        return;
      }
    }

    if (Element.isElement(node) && isList(node.type)) {
      // eslint-disable-next-line no-restricted-syntax
      for (const [child, childPath] of Node.children(editor, path)) {
        if (Element.isElement(child)) {
          // Flatten list blocks
          if (isList(child.type)) {
            Transforms.unwrapNodes(editor, { at: childPath });
            return;
          }
          // Ensure all children of list blocks are list-items
          if (child.type !== ElementTypes.listItem) {
            Transforms.setNodes(editor, { type: ElementTypes.listItem }, { at: childPath });
            return;
          }
        }
      }
    }

    normalizeNode(entry);
  };

  return editor;
};

export default withPlugin;
